import { useEffect } from "react";
import {
    Button,
    createStyles,
    Fab,
    Modal,
    Paper,
    Theme,
    Typography,
    withStyles,
    WithStyles,
    Zoom
} from "@material-ui/core";
import {
    CheckCircle,
    CheckCircleOutline,
    Error,
    Refresh
} from "@material-ui/icons";
import { HubConnectionState } from "@microsoft/signalr";
import posthog from "posthog-js";
import NumberFormat from "react-number-format";
import { connect } from "react-redux";
import { AnyAction, bindActionCreators, Dispatch } from "redux";
import { useGetConfigQuery } from "../../api/configApiSlice";
import ParcelApi from "../../api/ParcelApi";
import { trackWithMetaPixel } from "../../hooks/useMetaPixel";
import { useSignalR } from "../../hooks/useSignalR";
import { trackWithStackAdaptPixel } from "../../hooks/useStackAdaptPixel";
import useTimeoutFallbackEffect from "../../hooks/useTimedStateWatcher";
import Constants from "../../models/utility/Constants";
import { BidLoadState } from "../../models/utility/LoadState";
import { IAppState } from "../../store";
import * as ParcelBidStore from "../../store/ParcelBidStore";
import LoaderFab from "../shared/LoaderFab";

type ConfirmBidDialogProps = ParcelBidStore.IParcelBidStoreState &
    typeof ParcelBidStore.actionCreators &
    WithStyles<typeof styles>;

const WhiteButton = withStyles({
    label: {
        color: "#FFFFFF"
    }
})(Button);

const styles = (theme: Theme) =>
    createStyles({
        body: {
            [theme.breakpoints.down("xs")]: { padding: theme.spacing(2) },
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: theme.spacing(4),
            textAlign: "center"
        },
        bodyText: {
            [theme.breakpoints.down("xs")]: { fontSize: 20 },
            color: theme.palette.common.white,
            lineHeight: 1.5,
            marginBottom: theme.spacing(3),
            textAlign: "center"
        },
        bodyTextAmount: { fontWeight: "bold" },
        errorText: { color: theme.palette.error.main },
        fabButton: {
            height: theme.spacing(4),
            paddingLeft: theme.spacing(5),
            paddingRight: theme.spacing(5)
        },
        fabButtonWrapper: {
            [theme.breakpoints.down("xs")]: {
                marginBottom: theme.spacing(1.5)
            },
            marginBottom: theme.spacing(3)
        },
        header: {
            borderBottom: `8px solid ${theme.palette.primary.main}`,
            padding: theme.spacing(2),
            textAlign: "center",
            width: "100%"
        },
        headerIcon: {
            position: "relative",
            top: 3
        },
        headerText: {
            [theme.breakpoints.down("xs")]: { fontSize: 20 },
            color: theme.palette.primary.main,
            fontWeight: "bold",
            margin: "0 auto"
        },
        paper: {
            backgroundColor: "rgba(66,66,66,1)",
            boxShadow: theme.shadows[5],
            margin: theme.spacing(2),
            maxWidth: 500,
            outline: "none",
            width: "100%"
        },
        statusIcon: {
            [theme.breakpoints.down("xs")]: { fontSize: 40 },
            fontSize: 48
        },
        successText: {
            color: theme.palette.success.main,
            fontWeight: "bold"
        }
    });

const renderBidAmount = (parcelAmount: number) => (
    <NumberFormat
        value={parcelAmount}
        thousandSeparator={true}
        prefix="$"
        decimalScale={0}
        fixedDecimalScale={true}
        displayType="text"
    />
);

function ConfirmBidDialog({
    bidDialogVisible,
    classes,
    currentBid,
    resetCurrentBid,
    submitBid,
    toggleBidLoadState,
    toggleDialogVisible,
    updateCurrentBid
}: ConfirmBidDialogProps) {
    const getConfigQuery = useGetConfigQuery();
    const { signalRProviderConnectionState } = useSignalR();

    useEffect(() => {
        if (bidDialogVisible) {
            posthog.capture("BidDialog.Opened", { $set: { currentBid } });
        }
    }, [bidDialogVisible, currentBid]);

    useEffect(() => {
        if (currentBid.submitLoadState === BidLoadState.ErrorOutbid) {
            posthog.capture("BidDialog.OutBid", { $set: { currentBid } });
        }
    }, [currentBid.submitLoadState, currentBid]);

    const canConfirmBid =
        currentBid.submitLoadState === BidLoadState.NotSubmitted ||
        currentBid.submitLoadState === BidLoadState.Submitting;
    const showCloseButton = !canConfirmBid;
    const showRefreshButton =
        currentBid.submitLoadState === BidLoadState.ErrorOther;

    const closeDialog = () => {
        resetCurrentBid();
        toggleDialogVisible(false);
    };

    const submitBidClicked = () => {
        posthog.capture("BidDialog.BidPlaced", { $set: { currentBid } });
        trackWithMetaPixel("trackCustom", "PlaceBid", {
            bidAmount: currentBid.parcelAmount,
            parcelName: currentBid.parcelName
        });
        trackWithStackAdaptPixel(
            getConfigQuery.data?.stackAdaptPixelId,
            Constants.StackAdaptEvents.PlaceBid,
            {
                bidAmount: currentBid.parcelAmount,
                parcelName: currentBid.parcelName
            }
        );

        submitBid();
    };

    const onClickRefresh = () => window.location.reload();

    const startCondition =
        currentBid.submitLoadState === BidLoadState.Submitting &&
        currentBid.bidId != null;
    const endCondition = currentBid.submitLoadState !== BidLoadState.Submitting;
    const timeout = 5000;

    useTimeoutFallbackEffect(
        startCondition,
        endCondition,
        timeout,
        async () => {
            // TODO: Move this into a thunk and re-use error handling from ParcelStore.actionCreators.submitBid
            const { bidResponse } = await ParcelApi.GetBidStatus(
                currentBid.bidId
            );

            if (bidResponse != null) {
                toggleBidLoadState(bidResponse.result, bidResponse.errorText);

                updateCurrentBid(
                    bidResponse.parcelId,
                    bidResponse.result,
                    bidResponse.checkoutToken,
                    bidResponse.lastAcceptedBid,
                    bidResponse.errorText
                );
            }
        }
    );

    const isConfirmBidButtonDisabled =
        currentBid.submitLoadState === BidLoadState.Submitting ||
        signalRProviderConnectionState !== HubConnectionState.Connected;

    if (!bidDialogVisible) {
        return null;
    }

    return (
        <Modal
            open={bidDialogVisible}
            onClose={closeDialog}
            className={classes.body}
        >
            <Paper className={classes.paper} elevation={2}>
                {canConfirmBid && (
                    <div className={classes.header}>
                        <Typography
                            component="h1"
                            variant="h5"
                            className={classes.headerText}
                        >
                            <CheckCircleOutline
                                className={classes.headerIcon}
                            />
                            &nbsp;Confirm your Bid
                        </Typography>
                    </div>
                )}
                <div className={classes.body}>
                    {/* Confirm Text */}
                    <Zoom
                        in={canConfirmBid}
                        mountOnEnter={true}
                        unmountOnExit={true}
                    >
                        <Typography
                            component="div"
                            variant="h5"
                            className={classes.bodyText}
                        >
                            <div>Please confirm your bid for</div>
                            <div className={classes.bodyTextAmount}>
                                {renderBidAmount(currentBid.parcelAmount)} on{" "}
                                {currentBid.parcelName}
                            </div>
                        </Typography>
                    </Zoom>

                    {/* Successful Bid */}
                    <Zoom
                        in={currentBid.submitLoadState === BidLoadState.Success}
                        mountOnEnter={true}
                        unmountOnExit={true}
                    >
                        <Typography
                            component="div"
                            variant="h5"
                            className={`${classes.bodyText} ${classes.successText}`}
                        >
                            <div>
                                <CheckCircle className={classes.statusIcon} />
                            </div>
                            <div>Successfully placed bid!</div>
                        </Typography>
                    </Zoom>

                    {/* Error Outbid */}
                    <Zoom
                        in={
                            currentBid.submitLoadState ===
                            BidLoadState.ErrorOutbid
                        }
                        mountOnEnter={true}
                        unmountOnExit={true}
                    >
                        <Typography
                            component="div"
                            variant="h5"
                            className={`${classes.bodyText} ${classes.errorText}`}
                        >
                            <div>
                                <Error className={classes.statusIcon} />
                            </div>
                            <div>You have been outbid!</div>
                            <div>
                                The new amount on <b>{currentBid.parcelName}</b>{" "}
                                is{" "}
                                <b>
                                    {renderBidAmount(currentBid.parcelAmount)}
                                </b>
                                .
                            </div>
                            <div>Place new bid?</div>
                        </Typography>
                    </Zoom>

                    {/* Error No Time */}
                    <Zoom
                        in={
                            currentBid.submitLoadState ===
                            BidLoadState.ErrorNoTime
                        }
                        mountOnEnter={true}
                        unmountOnExit={true}
                    >
                        <Typography
                            component="div"
                            variant="h5"
                            className={`${classes.bodyText} ${classes.errorText}`}
                        >
                            <div>
                                <Error className={classes.statusIcon} />
                            </div>
                            <div>No time left!</div>
                            <div>
                                <b>{currentBid.parcelName}</b> has run out of
                                time.
                            </div>
                        </Typography>
                    </Zoom>

                    {/* Error Login */}
                    <Zoom
                        in={
                            currentBid.submitLoadState ===
                            BidLoadState.ErrorLogin
                        }
                        mountOnEnter={true}
                        unmountOnExit={true}
                    >
                        <Typography
                            component="div"
                            variant="h5"
                            className={`${classes.bodyText} ${classes.errorText}`}
                        >
                            <div>
                                <Error className={classes.statusIcon} />
                            </div>
                            <div>
                                {(currentBid.submitErrorText || "").toString()}
                            </div>
                            <div>
                                Please close this dialog and login to continue
                                bidding.
                            </div>
                        </Typography>
                    </Zoom>

                    {/* Error Access Denied */}
                    <Zoom
                        in={
                            currentBid.submitLoadState ===
                            BidLoadState.ErrorAccessDenied
                        }
                        mountOnEnter={true}
                        unmountOnExit={true}
                    >
                        <Typography
                            component="div"
                            variant="h5"
                            className={`${classes.bodyText} ${classes.errorText}`}
                        >
                            <div>
                                <Error className={classes.statusIcon} />
                            </div>
                            <div>
                                {(
                                    currentBid.submitErrorText ||
                                    "You are not authorized to bid on this parcel."
                                ).toString()}
                            </div>
                        </Typography>
                    </Zoom>

                    {/* Error Already High Bidder */}
                    <Zoom
                        in={
                            currentBid.submitLoadState ===
                            BidLoadState.ErrorAlreadyHighBidder
                        }
                        mountOnEnter={true}
                        unmountOnExit={true}
                    >
                        <Typography
                            component="div"
                            variant="h5"
                            className={`${classes.bodyText} ${classes.errorText}`}
                        >
                            <div>
                                <CheckCircle className={classes.statusIcon} />
                            </div>
                            <div>
                                {(
                                    currentBid.submitErrorText ||
                                    "You are already the high bidder on this parcel."
                                ).toString()}
                            </div>
                        </Typography>
                    </Zoom>

                    {/* Error Other */}
                    <Zoom
                        in={
                            currentBid.submitLoadState ===
                            BidLoadState.ErrorOther
                        }
                        mountOnEnter={true}
                        unmountOnExit={true}
                    >
                        <Typography
                            component="div"
                            variant="h5"
                            className={`${classes.bodyText} ${classes.errorText}`}
                        >
                            <div>
                                <Error className={classes.statusIcon} />
                            </div>
                            <div>An error has occurred.</div>
                            <div>
                                {(currentBid.submitErrorText || "").toString()}
                            </div>
                            <div>Please try refreshing your browser.</div>
                        </Typography>
                    </Zoom>

                    {/* Confirm Buttons */}
                    {canConfirmBid && (
                        <div>
                            <div className={classes.fabButtonWrapper}>
                                <LoaderFab
                                    className={classes.fabButton}
                                    variant="extended"
                                    color="primary"
                                    onClick={submitBidClicked}
                                    disabled={isConfirmBidButtonDisabled}
                                >
                                    Yes, Place Bid
                                </LoaderFab>
                            </div>
                            <WhiteButton onClick={closeDialog}>
                                Cancel
                            </WhiteButton>
                        </div>
                    )}

                    {/* Close Button */}
                    {showCloseButton && (
                        <div>
                            <Fab
                                className={classes.fabButton}
                                variant="extended"
                                color="primary"
                                onClick={closeDialog}
                            >
                                Close
                            </Fab>
                        </div>
                    )}

                    {/* Refresh Button */}
                    {showRefreshButton && (
                        <div>
                            <Fab
                                className={classes.fabButton}
                                variant="extended"
                                color="primary"
                                onClick={onClickRefresh}
                            >
                                <Refresh />
                                Refresh
                            </Fab>
                        </div>
                    )}
                </div>
            </Paper>
        </Modal>
    );
}

const reduxState = (state: IAppState) => state.parcelBidStoreState;
const reduxDispatch = (dispatch: Dispatch<AnyAction>) =>
    bindActionCreators(
        Object.assign({}, ParcelBidStore.actionCreators),
        dispatch
    );

export default withStyles(styles)(
    connect(reduxState, reduxDispatch)(ConfirmBidDialog)
);
